import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router/dom';
import { CircularProgress, Stack, ThemeProvider } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './store/authStore';
import { router } from './routes';
import { useAppTheme } from './theme/useTheme';
import { supabase } from './lib/supabase';
import 'react-toastify/dist/ReactToastify.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

export default function App() {
  const { checkSession } = useAuthStore();
  const theme = useAppTheme();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      // Check authentication session
      checkSession();

      // Mark as initialized
      setIsInitialized(true);
    };

    initialize();

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state change:', event, session?.user?.id);
      // Trigger checkSession to update the auth store
      checkSession();
    });

    return () => subscription.unsubscribe();
  }, [checkSession]);

  // Show simple loading indicator while initializing
  if (!isInitialized) {
    return (
      <ThemeProvider theme={theme}>
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{ height: '100dvh' }}
        >
          <CircularProgress />
        </Stack>
      </ThemeProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <RouterProvider router={router} />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </ThemeProvider>
    </QueryClientProvider>
  );
}
